// --- Our Modules --------------------------------------------------------------------------------
import useLang from "@/hooks/useLang";
import useTranslation from "@/hooks/useTranslation";
import type { TyInteraction, TyInteractionChannel } from "@/types";
import { dueState, formatDateTime } from "@/utils";
import Avatar from "@/UI/atoms/Avatar";
import Icon from "@/UI/atoms/Icon";
import type { TyIconName } from "@/UI/atoms/Icon/types";
import "./styles.css";
// --- Logic --------------------------------------------------------------------------------------
export type TyInteractionDetails = {
    interaction: TyInteraction;
    /** What it was about, already worded by the screen — an enquiry or a guarantee. */
    subject?: string | null;
    onEdit?: () => void;
    onRemove?: () => void;
};

type Props = { onClose: () => void; details?: unknown };

const CHANNEL_ICONS: Record<TyInteractionChannel, TyIconName> = {
    call: "phone",
    whatsapp: "phone",
    sms: "document",
    email: "document",
    visit: "building",
    catalog: "eye",
    other: "more",
};

/**
 * One contact, read whole.
 *
 * It exists because the card cannot be both. A timeline is scanned, so its rows are one line each
 * and the same height; what was actually said is a paragraph somebody wrote with a phone in their
 * hand, and it has to be readable somewhere. This is that somewhere.
 *
 * **The medallion is the whole header.** The channel is drawn big and tinted by state — green for a
 * contact that landed, red for one owed, gold for one booked — with the date arcing under it, so
 * the three facts a person opens this for are answered before they read a word. Everything that is
 * merely true about it sits below in a grid, and what was said gets the width of the panel.
 *
 * Editing is not done here. It opens the form, which is where the rules about what may still change
 * live — a contact whose date has passed accepts four fields and no more.
 */
export default function InteractionPopup({ onClose, details }: Props) {
    const t = useTranslation();
    const lang = useLang((state) => state.lang) ?? "ar";
    const { interaction, subject, onEdit, onRemove } = details as TyInteractionDetails;

    /** Both actions open another popup — the form, or the confirmation — so this one closes first. */
    const handle = (action: () => void) => () => {
        onClose();
        action();
    };

    const due = dueState(interaction);
    const author = typeof interaction.client === "object" && interaction.client ? interaction.client.name : null;
    const customer = typeof interaction.customer === "object" && interaction.customer ? interaction.customer : null;
    const isPlanned = due !== "done";

    /** The one word the header's ring reports, and the colour it is drawn in. */
    const state = interaction.status ?? (due === "overdue" ? "overdue" : "upcoming");
    const stateLabel = interaction.status ? t.crm.outcomes[interaction.status] : due === "overdue" ? t.crm.due.overdue : t.crm.due.upcoming;

    const facts: { key: string; icon: TyIconName; label: string; value: string }[] = [];
    if (!isPlanned) facts.push({ key: "direction", icon: "refresh", label: t.crm.fields.direction, value: t.crm.directions[interaction.direction] });
    if (interaction.category)
        facts.push({ key: "category", icon: "filter", label: t.crm.fields.category, value: t.crm.categories[interaction.category] });
    if (subject) facts.push({ key: "subject", icon: "crm", label: t.warranties.subject, value: subject });
    if (customer) facts.push({ key: "customer", icon: "team", label: t.crm.fields.name, value: customer.name });

    return (
        <section className={`interaction-popup interaction-popup--${state}`}>
            <button type="button" className="interaction-popup__close" onClick={onClose} aria-label={t.common.close}>
                <Icon name="close" size={16} />
            </button>

            <header className="interaction-popup__hero">
                <span className="interaction-popup__medallion" aria-hidden="true">
                    <Icon name={CHANNEL_ICONS[interaction.channel]} size={26} />
                </span>

                <h2 className="interaction-popup__title">{t.crm.channels[interaction.channel]}</h2>
                <p className="interaction-popup__when">{formatDateTime(interaction.date, lang)}</p>
                <span className="interaction-popup__state">{stateLabel}</span>
            </header>

            <div className="interaction-popup__body">
                {/* What was said, given the width it was written at. */}
                <blockquote className="interaction-popup__summary">
                    <Icon name="mic" size={14} />
                    <p>{interaction.summary || t.crm.noSummary}</p>
                </blockquote>

                {interaction.notes && <p className="interaction-popup__notes">{interaction.notes}</p>}

                {facts.length > 0 && (
                    <dl className="interaction-popup__facts">
                        {facts.map((fact) => (
                            <div key={fact.key} className="interaction-popup__fact">
                                <dt>
                                    <Icon name={fact.icon} size={12} />
                                    {fact.label}
                                </dt>
                                <dd>{fact.value}</dd>
                            </div>
                        ))}
                    </dl>
                )}

                {/* Whose it is. A face rather than a row in the grid: it is a person, not a value. */}
                <div className="interaction-popup__by">
                    <Avatar name={author ?? "?"} size={32} rounded />
                    <span>
                        <small>{t.crm.responsible}</small>
                        <strong>{author ?? t.crm.sources.catalog}</strong>
                    </span>
                </div>
            </div>

            {(onEdit || onRemove) && (
                <footer className="interaction-popup__actions">
                    {onEdit && (
                        <button type="button" className="interaction-popup__action interaction-popup__action--main" onClick={handle(onEdit)}>
                            <Icon name={isPlanned ? "check" : "edit"} size={14} />
                            {isPlanned ? t.crm.logWhatHappened : t.common.edit}
                        </button>
                    )}
                    {onRemove && (
                        <button type="button" className="interaction-popup__action interaction-popup__action--danger" onClick={handle(onRemove)}>
                            <Icon name="trash" size={14} />
                            {t.common.remove}
                        </button>
                    )}
                </footer>
            )}
        </section>
    );
}
